import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronsUpDown, Plus } from 'lucide-react';
import { ChangeEvent, Suspense, useEffect, useState } from 'react';
import { z } from 'zod';

import { AddIngredientModal } from '@/components/add-ingredient-modal';
import { Button } from '@/components/ui/button/button';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ViewContent, ViewHeader } from '@/components/view-layout';
import { WithAuthorization } from '@/components/with-authorization';
import { useDeleteIngredient } from '@/features/ingredients/api/ingredients-mutations';
import { useIngredients } from '@/features/ingredients/api/ingredients-queries';
import { IngredientTableActions } from '@/features/ingredients/components/ingredient-table-actions';
import { Ingredient } from '@/features/ingredients/types/ingredient-types';
import { useOrganisation } from '@/features/organisations/api/organisation-queries';
import { SupplierFilterButton } from '@/features/suppliers/components/supplier-filter-button';
import { useCurrencyFormatter } from '@/hooks/use-currency-formatter';

// TODO: Move the IngredientTable to the ingredients feature folder since seems like it doens't cross reference other features

const columnHelper = createColumnHelper<Ingredient>();

const makeColumns = (
  currencyFormatter?: Intl.NumberFormat,
  onDelete?: (id: string) => void,
) => [
  columnHelper.accessor('code', {
    header: 'Code',
    cell: (info) => info.getValue(),
    size: 20,
    filterFn: 'includesString',
  }),
  columnHelper.accessor('title', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Name
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 25,
  }),
  // TODO: Make this work with metric and imperial
  columnHelper.accessor('cost_per_kg', {
    id: 'ppu',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Price Per Kg
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) =>
      currencyFormatter?.format(info.getValue()) ?? info.getValue(),
    size: 15,
    minSize: 10,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('supplier_title', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Supplier
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 20,
    filterFn: (row, _, filterValue: string[]) => {
      return filterValue.length === 0
        ? true
        : filterValue.includes(row.original.supplier_id);
    },
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('recipe_count', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Recipe Count
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 15,
    minSize: 10,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const ingredient = row.original;
      return (
        <IngredientTableActions
          ingredient={ingredient}
          onDelete={onDelete ?? (() => {})} // eslint-disable-line @typescript-eslint/no-empty-function
        />
      );
    },
    size: 5,
    minSize: 5,
    enableSorting: false,
    enableHiding: false,
  }),
];

interface IngredientsTableProps {
  searchQuery: string;
  selectedSuppliers: string[];
}

function IngredientsTable({
  searchQuery,
  selectedSuppliers,
}: IngredientsTableProps) {
  const { tenantId } = Route.useParams();

  const navigate = Route.useNavigate();
  const { data: ingredients } = useIngredients(tenantId);
  const { data: organisation } = useOrganisation(tenantId);
  const deleteIngredient = useDeleteIngredient();

  if (!organisation) {
    // TODO: Handle this better, we should just throw an error in the hook and never get to here
    return <div>Error: Organisation not found</div>;
  }

  const currencyFormatter = useCurrencyFormatter(organisation.currency);

  const handleDelete = async (ingredientId: string) => {
    try {
      await deleteIngredient.mutateAsync({ id: ingredientId });
    } catch {
      // Handle error silently or show user notification
    }
  };

  const columns = makeColumns(currencyFormatter, handleDelete);

  const table = useReactTable({
    data: ingredients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    table.setGlobalFilter(searchQuery);
  }, [searchQuery, table]);

  useEffect(() => {
    table.setColumnFilters([
      {
        id: 'supplier_title',
        value: selectedSuppliers,
      },
    ]);
  }, [table, selectedSuppliers]);

  const handleRowClick = async (ingredient: Ingredient) => {
    await navigate({
      to: '/$tenantId/ingredients/$ingredientId',
      params: (prev) => ({
        ...prev,
        ingredientId: ingredient.id,
      }),
    });
  };

  return (
    <DataTable
      table={table}
      loading={false}
      skeletonRows={5}
      onClick={handleRowClick}
    />
  );
}

function IngredientsPage() {
  const navigate = Route.useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);

  const { search, suppliers } = Route.useSearch();

  const columns = makeColumns();

  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleImportCSV = () => {
    // TODO: Implement CSV import logic here
  };

  const handleSearchChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await navigate({
      search: (prev) => ({ ...prev, search: e.target.value }),
    });
  };

  const handleSelectedSuppliersChange = async (selectedSuppliers: string[]) => {
    await navigate({
      search: (prev) => ({ ...prev, suppliers: selectedSuppliers }),
    });
  };

  return (
    <>
      <ViewHeader title="Ingredients">
        <div className="flex gap-4 items-center">
          <SupplierFilterButton
            selectedSuppliers={suppliers}
            onSelectedSuppliersChange={handleSelectedSuppliersChange}
          />
          <Input
            placeholder="Search by code or name"
            value={search}
            onChange={handleSearchChange}
          />
          <WithAuthorization authorizedRoles={['admin', 'editor']}>
            <div className="flex items-center">
              <div className="inline-flex items-center border rounded-lg overflow-hidden">
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="rounded-r-none"
                >
                  <Plus className="h-4 w-4" />
                  Add ingredient
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" className="rounded-l-none">
                      <ChevronDown className="h-4 w-4 text-primary-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleImportCSV}>
                      Import CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </WithAuthorization>
        </div>
      </ViewHeader>
      <ViewContent>
        <Suspense
          fallback={<DataTable table={table} loading={true} skeletonRows={5} />}
        >
          <IngredientsTable
            searchQuery={search}
            selectedSuppliers={suppliers}
          />
        </Suspense>
      </ViewContent>
      <AddIngredientModal open={showAddModal} onOpenChange={setShowAddModal} />
    </>
  );
}

const ingredientListSearchSchema = z.object({
  search: z.string().default('').catch(''),
  suppliers: z.array(z.string()).default([]).catch([]),
});

export const Route = createFileRoute('/_authenticated/$tenantId/ingredients/')({
  validateSearch: ingredientListSearchSchema,
  component: IngredientsPage,
});
